// =============================================================================
// iOS Safari Cache Fix - Service Worker Cleanup + BFCache Detection
// =============================================================================

// Service Worker Killer - Enhanced with error handling
async function cleanupServiceWorkers(): Promise<void> {
  try {
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations()
      for (const registration of registrations) {
        try {
          const success = await registration.unregister()
          if (success && import.meta.env.DEV) console.log('[SW Cleanup] Unregistered:', registration.scope)
        } catch (e) {
          console.error('[SW Cleanup] Failed:', e)
        }
      }
    }
    if ('caches' in window) {
      const cacheNames = await caches.keys()
      for (const name of cacheNames) {
        try {
          await caches.delete(name)
          if (import.meta.env.DEV) console.log('[Cache Cleanup] Deleted:', name)
        } catch (e) {
          console.error('[Cache Cleanup] Failed:', e)
        }
      }
    }
  } catch (e) {
    console.error('[Cleanup] Error:', e)
  }
}

// BFCache Detection - Force reload on back-navigation (CRITICAL for iOS)
window.addEventListener('pageshow', (event) => {
  if (event.persisted) {
    if (import.meta.env.DEV) console.log('[BFCache] Restored from memory. Reloading.')
    window.location.reload()
  }
})

// Execute cleanup before Vue app initialization
cleanupServiceWorkers()

// =============================================================================
// Vue Application Setup
// =============================================================================

import { createApp, h } from 'vue'
import { createPinia } from 'pinia'
import { createVuetify } from 'vuetify'
import { vuetifyComponents } from '@/plugins/vuetifyComponents'
import type { IconSet, IconProps } from 'vuetify'
import { aliases } from 'vuetify/iconsets/mdi'
import * as mdiIcons from '@mdi/js'
import 'vuetify/styles'
// Import CSS variables FIRST (unified.scss is single source of truth)
import './styles/themes/unified.scss'
import './assets/styles/_button-tokens.scss'
import './assets/main.css'
import App from './App.vue'
import router from './router'

// Performance monitoring for PRD 4.2 (INP < 200ms)
import { initPerformanceMonitoring } from './utils/performance'

// Sentry error tracking
import * as Sentry from '@sentry/vue'

import { useAuthStore } from '@/stores/auth'

// MDI SVG icon set — converts 'mdi-axis-arrow' strings to crisp SVG paths
type MdiRecord = Record<string, string>
function mdiNameToJs(name: string): string {
  return 'mdi' + name.replace(/^mdi-/, '').split('-').map((s) => s.charAt(0).toUpperCase() + s.slice(1)).join('')
}
const mdiSvg: IconSet = {
  component: (props: IconProps) => {
    const path = (mdiIcons as MdiRecord)[mdiNameToJs(typeof props.icon === 'string' ? props.icon : '')] ?? ''
    return h('svg', { class: 'v-icon__svg', xmlns: 'http://www.w3.org/2000/svg', viewBox: '0 0 24 24', 'aria-hidden': 'true' }, [h('path', { d: path })])
  }
}

// Create app instance
const app = createApp(App)

interface WindowWithAnalytics {
  dataLayer?: unknown[]
  gtag?: (...args: unknown[]) => void
}

const initializeGA4 = (): void => {
  const measurementId = import.meta.env.VITE_GA_MEASUREMENT_ID
  if (!measurementId || typeof window === 'undefined') return

  const analyticsWindow = window as typeof window & WindowWithAnalytics

  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`
  document.head.appendChild(script)

  const dataLayer = analyticsWindow.dataLayer ?? []
  analyticsWindow.dataLayer = dataLayer

  const gtag = (...args: unknown[]): void => {
    dataLayer.push(args)
  }

  analyticsWindow.gtag = gtag
  gtag('js', new Date())
  gtag('config', measurementId)
}

initializeGA4()

// Initialize Sentry if DSN is configured (must be done after app creation but before mounting)
const sentryDsn = import.meta.env.VITE_SENTRY_DSN
if (sentryDsn) {
  Sentry.init({
    app,
    dsn: sentryDsn,
    environment: import.meta.env.MODE,
    release: `structengine-web@${import.meta.env.VITE_APP_VERSION || '1.0.0'}`,
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    // Performance Monitoring
    tracesSampleRate: import.meta.env.PROD ? 0.2 : 1.0,
    // Session Replay
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  })
}

// Resolve initial theme from localStorage / system preference before Vuetify init
// so defaultTheme matches the CSS class already applied by the blocking inline script
const _savedTheme = localStorage.getItem('theme-preference')
const _systemDark = window.matchMedia('(prefers-color-scheme: dark)').matches
const _initialTheme: 'dark' | 'light' =
  _savedTheme === 'light' || _savedTheme === 'dark'
    ? _savedTheme
    : _systemDark ? 'dark' : 'light'

// Create Vuetify instance with StructEngine dual theme
const vuetify = createVuetify({
  components: vuetifyComponents,
  theme: {
    defaultTheme: _initialTheme,
    themes: {
      dark: {
        dark: true,
        colors: {
          background: '#131a23',   // --bg-canvas
          surface: '#1f2731',   // --bg-surface
          primary: '#26c6da',   // --accent
          onPrimary: '#131a23',   // dark text on accent
          secondary: '#c5d0da',   // --text-body
          onBackground: '#ffffff',   // --text-primary
          onSurface: '#c5d0da',   // --text-body
          error: '#f35b69',   // --state-error
          success: '#44cf6c',   // --state-success
          info: '#26c6da',   // --accent
          warning: '#ffbd2e',   // --state-warning
        },
      },
      light: {
        dark: false,
        colors: {
          background: '#f8fafc',   // --bg-canvas (light)
          surface: '#ffffff',   // --bg-surface (light)
          primary: '#0077a3',   // --accent (light)
          onPrimary: '#ffffff',   // white text on accent
          secondary: '#475569',   // --text-body (light)
          onBackground: '#0f172a',   // --text-primary (light)
          onSurface: '#475569',   // --text-body (light)
          error: '#dc2626',   // --state-error (light)
          success: '#047857',   // --state-success (light)
          info: '#0077a3',   // --accent (light)
          warning: '#9a5800',   // --state-warning (light)
        },
      },
    },
  },
  icons: {
    defaultSet: 'mdi',
    aliases,
    sets: {
      mdi: mdiSvg,
    },
  },
})

// Use plugins
app.use(createPinia())
app.use(router)
app.use(vuetify)

// Fetch CSRF token proactively on app load
const authStore = useAuthStore()
authStore.fetchCsrfToken()

// Initialize performance monitoring (PRD 4.2 - INP < 200ms optimization)
if (import.meta.env.PROD) {
  // Production: minimal logging, send to analytics
  initPerformanceMonitoring({
    enableConsoleLogging: false,
    enableAnalytics: true,
    inpThreshold: 200
  })
} else {
  // Development: enable console logging for debugging
  initPerformanceMonitoring({
    enableConsoleLogging: true,
    enableAnalytics: false,
    inpThreshold: 200
  })
}

// Mount app
app.mount('#app')
